import { generateTestId } from './testIds'

/**
 * Create a secure test ID using array index
 * Use this instead of meaningful data like user IDs, names, etc.
 * Index-based IDs are more predictable, stable, and secure
 */
export const createIndexBasedTestId = (
  baseId: string,
  index: number,
  suffix?: string
): string => {
  return suffix ? `${baseId}-${suffix}-${index}` : `${baseId}-${index}`
}

/**
 * Create a secure test ID for list items
 * This prevents exposing sensitive data in test IDs by using array indices
 */
export const createSecureListTestId = (
  baseId: string,
  index: number,
  element: string
): string => {
  return `${baseId}-${element}-${index}`
}

/**
 * Enhanced test ID options similar to Angular's htmlId pattern
 */
export interface TestIdOptions {
  baseId?: string
  /** Feature name (e.g., 'organisation', 'residents') */
  feature?: string
  /** Component name (e.g., 'actions', 'table') */
  component?: string
  /** Action type (e.g., 'add', 'edit', 'delete') */
  action?: string
  /** Element type (e.g., 'btn', 'input', 'field') */
  elementType?: string
  /** Additional suffix */
  suffix?: string
  /** Index for lists/iterations */
  index?: number
  /** Custom ID override */
  customId?: string
}

/**
 * Creates a test ID using the enhanced pattern
 */
export const createTestId = (options: TestIdOptions): string => {
  const {
    baseId,
    feature,
    component,
    action,
    elementType,
    suffix,
    index,
    customId
  } = options

  // If customId is provided, use it directly
  // This allows for manual overrides
  if (customId) {
    return customId
  }

  // If baseId is provided, use it as the foundation
  // This allows for manual overrides
  if (baseId) {
    const parts = [baseId, suffix, index?.toString()].filter(Boolean)
    return parts.join('-')
  }

  // Fallback to existing pattern
  // This allows for automatic generation of test IDs
  return generateTestId(
    feature || '',
    action || '',
    elementType || '',
    component
  )
}

/**
 * Hook for generating test IDs with the enhanced pattern
 */
export const useTestId = (options: TestIdOptions): string => {
  return createTestId(options)
}

/**
 * Hook for generating test IDs with context awareness
 * This allows for components to know their context
 */
export const useContextualTestId = (
  baseId: string,
  context: string | null,
  action: string,
  elementType: string
): string => {
  const parts = [baseId, context, action, elementType].filter(Boolean)
  return parts.join('-')
}

/**
 * Hook for generating test IDs with component hierarchy awareness
 * This allows for deeply nested components to know their context
 */
export const useHierarchicalTestId = (
  baseId: string,
  hierarchy: string[],
  action: string,
  elementType: string
): string => {
  // Take the most relevant part of the hierarchy
  // This allows for deeply nested components to know their context
  const relevantContext =
    hierarchy.length > 0 ? hierarchy[hierarchy.length - 1] : null
  const parts = [baseId, relevantContext, action, elementType].filter(Boolean)
  return parts.join('-')
}

/**
 * Utility for creating test IDs for form fields
 * This allows for form fields to know their context
 */
export const createFieldTestId = (
  baseId: string,
  fieldName: string
): string => {
  return `${baseId}-${fieldName}`
}

/**
 * Utility for creating test IDs for table rows
 * This allows for table rows to know their context
 */
export const createRowTestId = (
  baseId: string,
  rowId: string | number
): string => {
  return `${baseId}-row-${rowId}`
}

/**
 * Utility for creating test IDs for modal elements
 * This allows for modal elements to know their context
 */
export const createModalTestId = (baseId: string, element: string): string => {
  return `${baseId}-modal-${element}`
}

/**
 * Utility for creating test IDs for navigation elements
 * This allows for navigation elements to know their context
 */
export const createNavTestId = (baseId: string, navItem: string): string => {
  return `${baseId}-nav-${navItem}`
}

/**
 * Common test ID patterns for consistency
 * This allows for consistent test IDs
 */
export const commonPatterns = {
  button: (baseId: string, action: string) => `${baseId}-${action}-btn`,
  input: (baseId: string, field: string) => `${baseId}-${field}-input`,
  label: (baseId: string, field: string) => `${baseId}-${field}-label`,
  field: (baseId: string, field: string) => `${baseId}-${field}-field`,
  row: (baseId: string, id: string | number) => `${baseId}-row-${id}`,
  modal: (baseId: string, element: string) => `${baseId}-modal-${element}`,
  tab: (baseId: string, tab: string) => `${baseId}-tab-${tab}`,
  card: (baseId: string, id: string | number) => `${baseId}-card-${id}`
} as const
